import React, { useMemo } from 'react';
import { Badge, Text, Group, Progress, Stack } from '@mantine/core';
import { ConfidenceIndicatorProps, ConfidenceLevel } from './ConfidenceIndicator.types';

/**
 * ConfidenceIndicator Component
 *
 * Displays a color-coded confidence score indicator
 *
 * Color scheme:
 * - 0-25%: red (Very Low)
 * - 26-50%: orange (Low)
 * - 51-75%: yellow (Medium)
 * - 76-90%: lime (High)
 * - 91-100%: green (Very High)
 *
 * Usage:
 * ```tsx
 * <ConfidenceIndicator score={0.85} />
 * <ConfidenceIndicator score={75} variant="progress" showLabel />
 * <ConfidenceIndicator score={0.5} variant="text" />
 * ```
 */
export const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  score,
  variant = 'badge',
  size = 'md',
  showPercentage = true,
  showLabel = false
}): React.ReactElement => {
  // Normalize score to 0-100 range
  const normalizedScore = useMemo((): number => {
    if (score <= 1) {
      return Math.round(score * 100);
    }
    return Math.round(score);
  }, [score]);

  // Get confidence level based on score
  const confidenceLevel = useMemo((): ConfidenceLevel => {
    // Confidence levels configuration
    const confidenceLevels: ConfidenceLevel[] = [
      { min: 0, max: 25, color: 'red', label: 'Very Low' },
      { min: 26, max: 50, color: 'orange', label: 'Low' },
      { min: 51, max: 75, color: 'yellow', label: 'Medium' },
      { min: 76, max: 90, color: 'lime', label: 'High' },
      { min: 91, max: 100, color: 'green', label: 'Very High' }
    ];

    const level = confidenceLevels.find(
      (l) => normalizedScore >= l.min && normalizedScore <= l.max
    );
    return level ?? confidenceLevels[0] ?? { min: 0, max: 100, color: 'gray', label: 'Unknown' };
  }, [normalizedScore]);

  // Format display text
  const displayText = useMemo((): string => {
    const percentText = showPercentage ? `${normalizedScore}%` : `${normalizedScore}`;
    if (showLabel) {
      return `${percentText} (${confidenceLevel.label})`;
    }
    return percentText;
  }, [normalizedScore, showPercentage, showLabel, confidenceLevel]);

  // Badge variant
  if (variant === 'badge') {
    return (
      <Badge color={confidenceLevel.color} variant="light" size={size}>
        {displayText}
      </Badge>
    );
  }

  // Text variant
  if (variant === 'text') {
    return (
      <Text size={size} c={`${confidenceLevel.color}.7`} fw={600}>
        {displayText}
      </Text>
    );
  }

  // Progress variant
  if (variant === 'progress') {
    return (
      <Stack gap="xs">
        <Progress
          value={normalizedScore}
          color={confidenceLevel.color}
          size={size}
          radius="sm"
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Confidence
          </Text>
          <Text size="xs" fw={600} c={`${confidenceLevel.color}.7`}>
            {displayText}
          </Text>
        </Group>
      </Stack>
    );
  }

  return <></>;
};
